import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Globals } from 'app/_globals';
import { SessionsService } from 'app/_services/sessions.service';
import { SiteService } from 'app/_services/site.service';
import DateTimeUtils from 'app/_utils/datetime.utils';
import { SessionPhotoModalComponent } from 'app/site-owner-sessions/session-photo-modal/session-photo-modal.component';
import { SiteFormModalComponent } from 'app/site-owner-sessions/worker-sessions/site-form-modal/site-form-modal.component';
import { environment } from 'environments/environment';
import { SortEvent } from 'primeng/api';
import { Subscription, interval } from 'rxjs';
import Swal from 'sweetalert2';

@Component({
  selector: 'site-site-sessions',
  templateUrl: './site-site-sessions.component.html'
})
export class SiteSiteSessionsComponent implements OnInit {
  private selectedSiteId: any;
  private routeRole: any;

  public selectedSite: any;
  public siteSessions = [];
  private updateSubscription: Subscription;
  public columnOptionsSidebarVisible: boolean;

  public togglableColumns: any[] = [
    { displayName: 'Guests', default: false },
    { displayName: 'Notes', default: true },
    { displayName: 'Photos', default: true },
    { displayName: 'Site Forms', default: true },
  ];

  // table options
  public tableOptions = {
    visibleColumns: [],
  }

  // date filters
  public toDate: Date;
  public fromDate: Date;

  // pagination
  public pageSizes: any = environment.pageSizes;
  public pagination: any;
  @ViewChild('paginator', { static: true }) paginator;

  // Sorting and Filtering
  public defaultSortField = 'startTime';
  public defaultSortOrder = -1;
  private sortOrderToText = (sortOrder: number) => { return sortOrder === 1 ? 'asc' : 'desc'; }
  public selectedSort = [this.defaultSortField + ',' + this.sortOrderToText(this.defaultSortOrder)];

  constructor(
    private siteService: SiteService,
    private sessionsService: SessionsService,
    private modalService: NgbModal,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    globals: Globals,
  ) {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.selectedSiteId = params.id;
    });

    this.routeRole = globals.user.routeRole;
  }

  ngOnInit(): void {
    this.toDate = DateTimeUtils.timelessDateAsUTCDate(new Date);
    this.fromDate = new Date(0);

    this.setUpSiteSpecificData();
    this.setupColumnOptions();
    this.resetPagination(); // call service to populate table

    // refresh data every 20 seconds
    this.updateSubscription = interval(20000).subscribe(() => {
      this.getPaginatedSiteSessions();
    });
  }

  ngOnDestroy(): void {
    if (this.updateSubscription) {
      this.updateSubscription.unsubscribe();
    }
  }

  private setUpSiteSpecificData(){
    this.siteService.getSiteById(this.selectedSiteId).subscribe((data: any) => {
      this.selectedSite = data;
    });
  }

  private getPaginatedSiteSessions() {
    const fromTimestamp = this.fromDate.getTime();
    const toTimestamp = this.toDate.getTime();

    this.sessionsService.getAllWorkerSessions(
      this.pagination,
      this.selectedSort,
      fromTimestamp,
      toTimestamp,
      this.selectedSiteId,
      undefined,
      undefined,
      'SITE'
    ).subscribe((data: any) => {
      this.siteSessions = data.body;

      this.pagination.totalRecords = Number(data.headers.get('X-Total-Count'));
    });
  }

  public paginate(event) {
    this.pagination.size = event.rows;
    this.pagination.page = event.page;
    this.getPaginatedSiteSessions();
  }

  private resetPagination() {
    this.pagination = {
      size: this.pageSizes[0],
      page: 0,
      totalRecords: 0,
    };
    this.goToFirstPage();
    this.getPaginatedSiteSessions();
  }

  public resetColumnOptions() {
    this.tableOptions = {
      visibleColumns: this.togglableColumns.filter(col => col.default).map(col => col.displayName),
    };

    this.saveTableOptions();
  }

  public saveTableOptions() {
    localStorage.setItem('ui.options.siteSiteSessions', JSON.stringify(this.tableOptions));
  }

  public setupColumnOptions() {
    const savedOptions = localStorage.getItem('ui.options.siteSiteSessions');

    if (savedOptions) {
      this.tableOptions = JSON.parse(savedOptions);
      this.togglableColumns
        .filter(col => col.default && !this.tableOptions.visibleColumns.includes(col.displayName))
        .forEach(col => this.tableOptions.visibleColumns.push(col.displayName));
      this.saveTableOptions();
    } else {
      this.resetColumnOptions();
    }
  }

  public customSort(sort: SortEvent) {
    const sortDirection = this.sortOrderToText(sort.order);

    this.selectedSort = [sort.field + ',' + sortDirection];

    this.goToFirstPage();

    this.getPaginatedSiteSessions();
  }

  private goToFirstPage() {
    this.pagination.page = 0;      // Backend: reset page index to fetch from API
    if (this.paginator) {
      this.paginator.first = 0;    // Frontend: update visual page indicator (PrimeNg)
    }
  }

  public viewNotes(notes) {
    Swal.fire({
      title: 'Session notes',
      text: notes,
      type: 'info'
    });
  }

  public viewPhotos(sessionId) {
    const modalRef = this.modalService.open(SessionPhotoModalComponent, { size: 'lg' });
    modalRef.componentInstance.sessionId = sessionId;
  }

  public viewSiteForms(session) {
    const modalRef = this.modalService.open(SiteFormModalComponent, { size: 'lg' });
    modalRef.componentInstance.sessionId = session.id;
    modalRef.componentInstance.siteOwnerLogin = session.siteSession.site.siteOwner.login;
  }
}
